import React, { useState, useEffect, useRef } from 'react'
import dayjs from 'dayjs'
import { Tooltip } from 'react-tooltip'
import moment from 'moment-timezone'
import { LockClosedIcon, PencilIcon } from '@heroicons/react/24/solid'

import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'

import { useDarkMode } from '../../context/DarkModeContext'
import {
	colorBasedOnAge,
	formatLastSeenDate,
	dateDifference,
	truncateString,
} from './loadboardHelpers.js'
import './load.css'

import { loadAnalytics, getLoadPriceChanges } from '../../services/httpService'

import LoadCollapse from './LoadCollapseContent/LoadCollapse'
import QuickPostButton from './PostATruckModal/QuickPostButton'

const capitalName = str => {
	return str
		?.toLowerCase()
		?.split(' ')
		?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		?.join(' ')
}

function formatRelativeTime(timestamp) {
	const now = new Date()
	const date = new Date(timestamp)
	const diff = now.getTime() - date.getTime()
	const seconds = Math.floor(diff / 1000)
	const minutes = Math.floor(seconds / 60)
	const hours = Math.floor(minutes / 60)
	const days = Math.floor(hours / 24)

	if (minutes < 1) {
		return '1m ago'
	} else if (minutes < 60) {
		return `${minutes}m ago`
	} else if (hours < 24) {
		return `${hours}hr ago`
	} else {
		return `${days}d ago`
	}
}

dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(advanced)
dayjs.tz.guess()

export default function LoadCard({ load, originLat, originLng, onPostTruck, showQuickPost, onFacilityClick, isMobile, isTablet, defaultExpanded = false, hideShare = false, locked = false, lockedContent = null, isSubscribed = true, onUpgradeRequired }) {
	const [isCollapsed, setIsCollapsed] = useState(!defaultExpanded)
	const [hoverPickup, setHoverPickup] = useState(false)
	const [hoverDropoff, setHoverDropoff] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isQuickPostActive, setIsQuickPostActive] = useState(false)

	const [pickUpAnalytics, pickUpSetAnalytics] = useState({})
	const [dropOffAnalytics, dropOffSetAnalytics] = useState({})
	const [priceChanges, setPriceChanges] = useState(null)
	const [priceChangesLoading, setPriceChangesLoading] = useState(false)
	const [hoverPrice, setHoverPrice] = useState(false)

	const lastClickTimeRef = useRef(0)

	const { isDarkMode } = useDarkMode()

	const {
		pickup,
		dropOff,
		lastSeen,
		price,
		distance,
		equipment,

		createdAt,
		driverType,
		loadingType,
		trailerTypes,
		trailerProvided,
		priorityBadge,
		endPriorityTime,
		trailerReady,
		isCertRequired,
	} = load

	const pickupCity = `${truncateString(pickup?.address?.city, 13)}, ${
		pickup.address.state
	}`

	const pickupLabel = truncateString(load?.pickupLabel, 4)
	const puDate = pickup?.date_local

	let pickupTime = pickup?.timezone
		? moment(pickup?.date_local)
				.tz(pickup?.timezone)
				.format('ddd MMM DD HH:mm z')
		: moment(pickup?.date_local).format('ddd MMM DD HH:mm z')

	const deliveryCity = `${truncateString(dropOff?.address?.city, 13)}, ${
		dropOff?.address?.state
	}`
	const deliveryLabel = truncateString(load?.deliveryLabel, 4)

	const delDate = dropOff?.date_local

	let deliveryDate = dropOff?.timezone
		? moment(dropOff?.date_local)
				.tz(dropOff.timezone)
				.format('ddd MMM DD HH:mm z')
		: moment(dropOff.date_local).format('ddd MMM DD HH:mm z')

	const loadStops = load?.stopCount

	const pricePerMile = price / distance

	const priceText = locked ? '\u2014' : '$' + (price ? price?.toFixed(2) : 0)
	const ppmText = locked
		? 'Log in to see rate'
		: '$' + (pricePerMile ? pricePerMile?.toFixed(2) : 0) + '/mi'

	const getBackgroundColor = () => {
		if (load.isNewLoad) {
			return isDarkMode ? '#313866' : '#C8FFE0'
		}
		if (load.isPriceIncrease) {
			return isDarkMode ? '#313866' : '#C8FFE0'
		}

		return isDarkMode ? '#22272e' : '#ffffff'
	}

	const styles = isDarkMode
		? {
				backgroundColor: getBackgroundColor(),
				color: '#adbac7',
				borderColor: '#374151',
		  }
		: {
				backgroundColor: getBackgroundColor(),
				color: '#000000',
				borderColor: '#d1d5db',
		  }

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed)
	}

	const handleRowClick = () => {
		const now = Date.now()
		const timeSinceLastClick = now - lastClickTimeRef.current

		if (timeSinceLastClick < 300) {
			setIsQuickPostActive(true)
		} else {
			toggleCollapse()
		}

		lastClickTimeRef.current = now
	}

	const handlePriceTap = e => {
		e.stopPropagation()
		setHoverPrice(true)
	}

	const handlePickupTap = e => {
		e.stopPropagation()
		setHoverPickup(true)
	}

	const handleDropoffTap = e => {
		e.stopPropagation()
		setHoverDropoff(true)
	}

	const lastSeenStyle = colorBasedOnAge(lastSeen)
	const lastSeenDate = formatLastSeenDate(lastSeen)
	const transitDiff = dateDifference(puDate, delDate)

	const formatLoadingType = type => {
		if (!type) return 'N/A'
		if (type === 'Live') return 'Live'
		if (type === 'Drop & Hook') return 'Drop'
		if (type.includes('Live') && type.includes('Drop')) return 'Live/Drop'
		return type
	}

	const formatTrailerTypes = types => {
		if (!types || !Array.isArray(types) || types.length === 0)
			return capitalName(equipment) || 'N/A'

		const formattedTypes = types.map(type => {
			if (type.startsWith('Fifty Three') || type.startsWith('fifty three')) {
				return type.replace(/^Fifty Three/i, '53')
			}
			return type
		})

		const joinedTypes = formattedTypes.join(', ')
		return joinedTypes.length > 27
			? joinedTypes.substring(0, 27) + '...'
			: joinedTypes
	}

	const calculateDeadhead = () => {
		// MULTI-ORIGIN DEADHEAD: Use matchedOrigin if available, otherwise fallback to originLat/originLng
		const originLatToUse = load.matchedOrigin?.lat || originLat
		const originLngToUse = load.matchedOrigin?.lng || originLng

		if (!originLatToUse || !originLngToUse || !pickup?.location?.coordinates)
			return null

		const [pickupLng, pickupLat] = pickup.location.coordinates
		const R = 3959 // Earth's radius in miles

		const lat1 = (originLatToUse * Math.PI) / 180
		const lat2 = (pickupLat * Math.PI) / 180
		const deltaLat = ((pickupLat - originLatToUse) * Math.PI) / 180
		const deltaLon = ((pickupLng - originLngToUse) * Math.PI) / 180

		const a =
			Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
			Math.cos(lat1) *
				Math.cos(lat2) *
				Math.sin(deltaLon / 2) *
				Math.sin(deltaLon / 2)

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
		const deadheadDistance = R * c

		return deadheadDistance.toFixed(0)
	}

	const deadheadDistance = calculateDeadhead()

	useEffect(() => {
		if (!hoverPickup) {
			setIsLoading(false)
			return
		}

		setIsLoading(true)
		let cancelled = false
		const timeoutId = setTimeout(async () => {
			const [latitude, longitude] = pickup.location.coordinates
			const response = await loadAnalytics(
				`${latitude}, ${longitude}`,
				equipment
			)
			if (!cancelled) {
				pickUpSetAnalytics(response?.data)
				setIsLoading(false)
			}
		}, 1000)

		return () => {
			cancelled = true
			clearTimeout(timeoutId)
		}
	}, [hoverPickup])

	useEffect(() => {
		if (!hoverDropoff) {
			setIsLoading(false)
			return
		}

		setIsLoading(true)
		let cancelled = false
		const timeoutId = setTimeout(async () => {
			const [latitude, longitude] = dropOff.location.coordinates
			const response = await loadAnalytics(
				`${latitude}, ${longitude}`,
				equipment
			)
			if (!cancelled) {
				dropOffSetAnalytics(response?.data)
				setIsLoading(false)
			}
		}, 1000)

		return () => {
			cancelled = true
			clearTimeout(timeoutId)
		}
	}, [hoverDropoff])

	useEffect(() => {
		if (!hoverPrice || priceChanges !== null) {
			setPriceChangesLoading(false)
			return
		}

		setPriceChangesLoading(true)
		let cancelled = false
		const timeoutId = setTimeout(async () => {
			const data = await getLoadPriceChanges(load._id)
			if (!cancelled) {
				setPriceChanges(data.priceChanges || [])
				setPriceChangesLoading(false)
			}
		}, 1000)

		return () => {
			cancelled = true
			clearTimeout(timeoutId)
		}
	}, [hoverPrice])

	const pickupTip = isLoading ? (
		<div>Loading...</div>
	) : (
		<div>
			<h3>Last 24 hours in {pickupCity}:</h3>
			<ul>
				<li>{pickUpAnalytics?.pickupsCount || 0} pickups</li>
				<li>{pickUpAnalytics?.deliveriesCount || 0} deliveries</li>
				<li>{pickUpAnalytics?.truckCount || 0} truck count</li>
			</ul>
		</div>
	)

	const dropoffTip = isLoading ? (
		<div>Loading...</div>
	) : (
		<div>
			<h3>Last 24 hours in {deliveryCity}:</h3>
			<ul>
				<li>{dropOffAnalytics?.pickupsCount || 0} pickups</li>
				<li>{dropOffAnalytics?.deliveriesCount || 0} deliveries</li>
				<li>{dropOffAnalytics?.truckCount || 0} truck count</li>
			</ul>
		</div>
	)

	const createdAtDate = formatLastSeenDate(createdAt)
	const lastSeenDateFormatted = formatLastSeenDate(lastSeen)

	const lastSeenTip = (
		<div>
			<h3>Last Seen: {lastSeenDateFormatted} ago</h3>
			<h3>Created: {createdAtDate} ago</h3>
		</div>
	)

	const isPriorityActive = () => {
		if (!priorityBadge) return false
		if (!endPriorityTime) return true

		const now = new Date()
		const endTime = new Date(endPriorityTime)
		const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

		// Don't show priority badge if end time is more than 7 days in the future
		if (endTime > sevenDaysFromNow) return false

		return endTime > now
	}

	const getPriorityMessage = () => {
		if (!endPriorityTime) return 'Priority Load'

		const now = new Date()
		const endTime = new Date(endPriorityTime)
		const diff = endTime - now

		if (diff <= 0) {
			return 'Available to everyone'
		}

		const days = Math.floor(diff / (1000 * 60 * 60 * 24))
		const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
		const seconds = Math.floor((diff % (1000 * 60)) / 1000)

		if (days > 0) {
			return `Available to more carriers in ${days}d ${hours}h`
		} else if (hours > 0) {
			return `Available to more carriers in ${hours}h ${minutes}m`
		} else {
			return `Available to more carriers in ${minutes}m ${seconds}s`
		}
	}

	const showPriorityBadge = isPriorityActive()

	const priorityTip = showPriorityBadge ? (
		<div>
			<h3>{getPriorityMessage()}</h3>
		</div>
	) : null

	const arrivalWindowsTip =
		load?.arrivalWindows && load.arrivalWindows.length > 0 ? (
			<div>
				<h3>This trip has an adjustable pickup date</h3>
				<p style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>
					Range:{' '}
					{load.arrivalWindows.map((window, i) => {
						const pickupTimezone = pickup?.timezone || 'America/New_York'
						const startDate = pickup?.timezone
							? moment(window.start)
									.tz(pickupTimezone)
									.format('ddd MMM DD HH:mm z')
							: moment(window.start).format('ddd MMM DD HH:mm z')
						const endDate = pickup?.timezone
							? moment(window.end)
									.tz(pickupTimezone)
									.format('ddd MMM DD HH:mm z')
							: moment(window.end).format('ddd MMM DD HH:mm z')
						return (
							<span key={i}>
								{i > 0 && <br />}
								{startDate} - {endDate}
							</span>
						)
					})}
				</p>
			</div>
		) : null

	const priceTip = priceChangesLoading ? (
		<div>
			<p style={{ color: '#9ca3af', fontSize: 12 }}>Loading...</p>
		</div>
	) : priceChanges?.length > 0 ? (
		<div style={{ maxHeight: 500, overflowY: 'auto' }}>
			<h3 style={{ marginBottom: 8 }}>Price History:</h3>
			{priceChanges
				.slice()
				.reverse()
				.slice(0, 15)
				.map((change, i) => (
					<div
						key={i}
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							marginBottom: 4,
							gap: 16,
						}}
					>
						<span style={{ fontWeight: 600 }}>
							${change.price?.toFixed(2) || 0}
						</span>
						<span style={{ color: '#9ca3af', fontSize: 12 }}>
							{formatRelativeTime(change.time)}
						</span>
					</div>
				))}
		</div>
	) : (
		<div>
			<h3 style={{ marginBottom: 8 }}>Price History:</h3>
			<p style={{ color: '#9ca3af', fontSize: 12 }}>
				{isMobile ? 'Tap' : 'Hover'} to load price history
			</p>
		</div>
	)

	const isTrailerWarning = trailerTypes?.includes("26' Truck") && trailerProvided === 'Provided'
	const isTrailerRequiredWarning =
		(equipment?.toLowerCase() === 'power only' || equipment?.toLowerCase() === 'dry van') &&
		trailerProvided === 'Required'

	return (
		<div className="py-0.5 load-card">
			<div
				onClick={handleRowClick}
				className={`border shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-md grid grid-cols-12 items-center gap-3 px-3 py-2 ${
					isCollapsed ? 'rounded-lg' : 'rounded-t-lg border-b-0'
				} cursor-pointer transition-all`}
				style={styles}
			>
				{isMobile ? (
				<div className="col-span-12">
					{(trailerReady || isCertRequired) && (
						<div className="mb-1 flex flex-wrap gap-1">
							{trailerReady && (
								<span className={`text-[9px] px-1.5 py-0.5 rounded text-white font-medium ${
									isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
								}`}>
									Trailer ready
								</span>
							)}
							{isCertRequired && (
								<span className={`text-[9px] px-1.5 py-0.5 rounded text-white font-medium ${
									isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
								}`}>
									Certificate
								</span>
							)}
						</div>
					)}

					<div className="flex gap-3">
						<div className="min-w-0 flex-1">
							<p className="text-xs">
								<span className="text-sm">1. </span>
								{pickupLabel && (
									pickupLabel.length <= 4 ? (
										<span
											className="underline decoration-dotted cursor-pointer"
											onClick={e => {
												e.stopPropagation()
												onFacilityClick?.(load.pickupLabel)
											}}
										>
											{pickupLabel}
										</span>
									) : (
										<span>{pickupLabel}</span>
									)
								)}{' '}
								<span
									data-tooltip-delay-show="0"
									data-tooltip-place="top"
									data-tooltip-id={load._id}
									data-tooltip-content="pickup"
									onClick={handlePickupTap}
								>
									{pickupCity}
								</span>
							</p>

							<div className="flex min-w-0 items-center gap-1">
								{load?.arrivalWindows && load.arrivalWindows.length > 0 && (
									<PencilIcon
										className="w-3 h-3"
										style={{
											color: isDarkMode ? '#adbac7' : '#6b7280',
											minWidth: '12px',
											minHeight: '12px',
										}}
										data-tooltip-delay-show="500"
										data-tooltip-place="top"
										data-tooltip-id={`${load._id}-arrival-windows`}
										data-tooltip-content="arrival"
									/>
								)}
								<p className="text-gray-500 text-[10px]">{pickupTime}</p>
							</div>
						</div>

						<div className="flex shrink-0 flex-col items-end pt-0.5">
							<div className="flex items-center justify-end gap-1">
								<p
									className="text-sm text-right font-semibold whitespace-nowrap"
									style={load.isPriceIncrease ? { color: isDarkMode ? '#4ade80' : '#16a34a' } : undefined}
									data-tooltip-delay-show="0"
									data-tooltip-place="top"
									data-tooltip-id={`${load._id}-price`}
									data-tooltip-content="price"
									onClick={handlePriceTap}
								>
									{priceText}
								</p>
								{showPriorityBadge && (
									<LockClosedIcon
										className="w-4 h-4"
										style={{
											color: isDarkMode ? '#ffffff' : '#000000',
											minWidth: '16px',
											minHeight: '16px',
										}}
										data-tooltip-delay-show="500"
										data-tooltip-place="top"
										data-tooltip-id={`${load._id}-priority`}
										data-tooltip-content="priority"
									/>
								)}
							</div>
							<p
								className="text-gray-500 text-[10px] text-right whitespace-nowrap"
								data-tooltip-delay-show="0"
								data-tooltip-place="top"
								data-tooltip-id={`${load._id}-price`}
								data-tooltip-content="price"
								onClick={handlePriceTap}
							>
								{load.isPriceIncrease && (
									<>
										<span style={{ color: isDarkMode ? '#4ade80' : '#16a34a' }}>
											↑${Math.ceil(load.priceDifference)}
										</span>{' '}
										<span className="text-gray-400 line-through">
											${(price - load.priceDifference)?.toFixed(2)}
										</span>{' '}
									</>
								)}
								{ppmText}
							</p>
						</div>
					</div>

					<div className="mt-1.5 mb-0.5 text-[10px] leading-none text-gray-500">
						<span>Deadhead</span>
						<span className="ml-1">{deadheadDistance || 0} mi</span>
					</div>

					<div className="flex gap-3">
						<div className="min-w-0 flex-1">
							<p className="text-xs">
								<span className="text-sm">{loadStops}. </span>
								{deliveryLabel && (
									deliveryLabel.length <= 4 ? (
										<span
											className="underline decoration-dotted cursor-pointer"
											onClick={e => {
												e.stopPropagation()
												onFacilityClick?.(load.deliveryLabel)
											}}
										>
											{deliveryLabel}
										</span>
									) : (
										<span>{deliveryLabel}</span>
									)
								)}{' '}
								<span
									data-tooltip-delay-show="0"
									data-tooltip-place="top"
									data-tooltip-id={load._id}
									data-tooltip-content="dropoff"
									onClick={handleDropoffTap}
								>
									{deliveryCity}
								</span>
							</p>

							<p className="text-gray-500 text-[10px]">{deliveryDate}</p>
						</div>

						<div className="flex shrink-0 items-center justify-end gap-1 pt-0.5">
							<p className="text-gray-500 text-[11px] text-right">Last seen</p>
							<p
								style={{
									...lastSeenStyle,
									width: '30px',
									height: '18px',
									fontSize: '11px',
									lineHeight: 1,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: '0 3px',
								}}
								data-tooltip-delay-show="800"
								data-tooltip-place="top"
								data-tooltip-id={load._id}
								data-tooltip-content="lastSeen"
							>
								{lastSeenDate}
							</p>
						</div>
					</div>

					<div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] leading-tight text-gray-500">
						<span>{distance ? distance?.toFixed(0) : 0} miles</span>
						<span>·</span>
						<span>{transitDiff}</span>
						<span>·</span>
						<span>{driverType || 'N/A'}</span>
						<span>·</span>
						<span
							className={`${
								isTrailerWarning
									? 'text-red-500 font-semibold'
									: isDarkMode
									? 'text-gray-300'
									: 'text-gray-700'
							}`}
						>
							{formatTrailerTypes(trailerTypes)}
							{isTrailerWarning && <span className="ml-1">!</span>}
						</span>
						<span
							className={`rounded-md px-1.5 py-0.5 ${
								isTrailerRequiredWarning
									? 'bg-red-100 text-red-600 font-semibold'
									: isDarkMode
									? 'bg-gray-700 text-gray-200'
									: 'bg-gray-200 text-gray-700'
							}`}
						>
							{isTrailerRequiredWarning && <span>! </span>}
							{trailerProvided || 'Trailer'}
						</span>
						<span>·</span>
						<span>{formatLoadingType(loadingType)}</span>
						{load?.isBobtail && (
							<span
								className={`${
									isDarkMode ? 'text-blue-400' : 'text-blue-600'
								} font-medium`}
							>
								Bobtail
							</span>
						)}
						{load?.isAIEnabled && (
							<span
								className={`${
									isDarkMode ? 'text-purple-400' : 'text-purple-500'
								} font-medium`}
							>
								AI
							</span>
						)}
					</div>
				</div>
				) : isTablet ? (
				<div className="col-span-12">
					{/* Row 1: route + price */}
					<div className="flex items-start gap-3">
						<div className="flex min-w-0 flex-1 items-center gap-3">
							<div className="flex min-w-0 flex-1 items-center gap-2">
							<div className="min-w-0">
								<div className="flex min-w-0 items-center gap-1.5">
									<span className="badge badge-accent h-5 w-5 min-h-0 shrink-0 rounded-full p-0 text-[11px] font-semibold leading-none">
										1
									</span>
									<p
										className="truncate text-xs"
										data-tooltip-delay-show="800"
										data-tooltip-place="top"
										data-tooltip-id={load._id}
										data-tooltip-content="pickup"
										onMouseEnter={() => setHoverPickup(true)}
										onMouseLeave={() => setHoverPickup(false)}
									>
										{pickupLabel && (
											pickupLabel.length <= 4 ? (
												<span
													className="underline decoration-dotted cursor-pointer"
													onClick={e => {
														e.stopPropagation()
														onFacilityClick?.(load.pickupLabel)
													}}
												>
													{pickupLabel}
												</span>
											) : (
												<span>{pickupLabel}</span>
											)
										)}{' '}
										{pickupCity}
									</p>
								</div>
								<div className="flex items-center gap-1 text-gray-500 text-[11px]">
									{load?.arrivalWindows && load.arrivalWindows.length > 0 && (
										<PencilIcon
											className="w-3 h-3"
											style={{
												color: isDarkMode ? '#adbac7' : '#6b7280',
												minWidth: '12px',
												minHeight: '12px',
											}}
											data-tooltip-delay-show="500"
											data-tooltip-place="top"
											data-tooltip-id={`${load._id}-arrival-windows`}
											data-tooltip-content="arrival"
										/>
									)}
									<span>{pickupTime}</span>
								</div>
							</div>

							<div className="shrink-0 text-center leading-tight">
								<p className={`text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
									{deadheadDistance || 0} mi
								</p>
								<p className="text-[10px] text-gray-500">DH</p>
							</div>
							</div>

							<div className="flex min-w-0 flex-1 items-center gap-2">
							<div className="min-w-0">
								<div className="flex min-w-0 items-center gap-1.5">
									<span className="badge badge-accent h-5 w-5 min-h-0 shrink-0 rounded-full p-0 text-[11px] font-semibold leading-none">
										{loadStops}
									</span>
									<p
										className="truncate text-xs"
										data-tooltip-delay-show="800"
										data-tooltip-place="top"
										data-tooltip-id={load._id}
										data-tooltip-content="dropoff"
										onMouseEnter={() => setHoverDropoff(true)}
										onMouseLeave={() => setHoverDropoff(false)}
									>
										{deliveryLabel && (
											deliveryLabel.length <= 4 ? (
												<span
													className="underline decoration-dotted cursor-pointer"
													onClick={e => {
														e.stopPropagation()
														onFacilityClick?.(load.deliveryLabel)
													}}
												>
													{deliveryLabel}
												</span>
											) : (
												<span>{deliveryLabel}</span>
											)
										)}{' '}
										{deliveryCity}
									</p>
								</div>
								<p className="text-gray-500 text-[11px]">{deliveryDate}</p>
							</div>

							<div className="shrink-0 text-center leading-tight">
								<p
									style={{
										...lastSeenStyle,
										fontSize: '11px',
										lineHeight: 1,
										padding: '2px 5px',
										borderRadius: '4px',
										display: 'inline-block',
									}}
									data-tooltip-delay-show="800"
									data-tooltip-place="top"
									data-tooltip-id={load._id}
									data-tooltip-content="lastSeen"
								>
									{lastSeenDate}
								</p>
								<p className="text-[10px] text-gray-500">Last seen</p>
							</div>
							</div>
						</div>

						<div className="flex shrink-0 items-center gap-2">
							<div className="flex flex-col items-end">
								<div className="flex items-center gap-1">
									<p
										className={`text-xs whitespace-nowrap ${
											load.isPriceIncrease ? 'font-bold' : ''
										}`}
										style={
											load.isPriceIncrease
												? { color: isDarkMode ? '#4ade80' : '#16a34a' }
												: undefined
										}
										data-tooltip-delay-show="500"
										data-tooltip-place="left"
										data-tooltip-id={`${load._id}-price`}
										data-tooltip-content="price"
										onMouseEnter={() => setHoverPrice(true)}
										onMouseLeave={() => setHoverPrice(false)}
									>
										{priceText}
									</p>
									{showPriorityBadge && (
										<LockClosedIcon
											className="w-4 h-4"
											style={{
												color: isDarkMode ? '#ffffff' : '#000000',
												minWidth: '16px',
												minHeight: '16px',
											}}
											data-tooltip-delay-show="500"
											data-tooltip-place="top"
											data-tooltip-id={`${load._id}-priority`}
											data-tooltip-content="priority"
										/>
									)}
								</div>
								<p
									className="text-gray-500 text-[11px] whitespace-nowrap"
									data-tooltip-delay-show="500"
									data-tooltip-place="left"
									data-tooltip-id={`${load._id}-price`}
									data-tooltip-content="price"
									onMouseEnter={() => setHoverPrice(true)}
									onMouseLeave={() => setHoverPrice(false)}
								>
									{load.isPriceIncrease && (
										<>
											<span style={{ color: isDarkMode ? '#4ade80' : '#16a34a' }}>
												↑${Math.ceil(load.priceDifference)}
											</span>{' '}
											<span className="text-gray-400 line-through">
												${(price - load.priceDifference)?.toFixed(2)}
											</span>{' '}
										</>
									)}
									{ppmText}
								</p>
							</div>
							{showQuickPost && (
								<QuickPostButton
									load={load}
									isDarkMode={isDarkMode}
									isActive={isQuickPostActive}
									onActivate={() => setIsQuickPostActive(true)}
									onDeactivate={() => setIsQuickPostActive(false)}
								/>
							)}
						</div>
					</div>

					{/* Row 2: meta */}
					<div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] leading-tight text-gray-500">
						<span>
							{distance ? distance?.toFixed(0) : 0} mi {transitDiff}
						</span>
						<span>·</span>
						<span>{driverType || 'N/A'}</span>
						<span>·</span>
						<span
							className={`${
								isTrailerWarning
									? 'text-red-500 font-semibold'
									: isDarkMode
									? 'text-gray-300'
									: 'text-gray-700'
							}`}
						>
							{formatTrailerTypes(trailerTypes)}
							{isTrailerWarning && <span className="ml-1">!</span>}
						</span>
						<span
							className={`rounded-md px-1.5 py-0.5 ${
								isTrailerRequiredWarning
									? 'bg-red-100 text-red-600 font-semibold'
									: isDarkMode
									? 'bg-gray-700 text-gray-200'
									: 'bg-gray-200 text-gray-700'
							}`}
						>
							{isTrailerRequiredWarning && <span>! </span>}
							{trailerProvided || 'Trailer'}
						</span>
						<span>·</span>
						<span>{formatLoadingType(loadingType)}</span>
						{load?.isBobtail && (
							<span className={`${isDarkMode ? 'text-blue-400' : 'text-blue-600'} font-medium`}>
								Bobtail
							</span>
						)}
						{load?.isAIEnabled && (
							<span className={`${isDarkMode ? 'text-purple-400' : 'text-purple-500'} font-medium`}>
								AI
							</span>
						)}
					</div>
				</div>
				) : (
				<>
				<div className="md:block md:col-span-1">
					{trailerReady && (
						<div className="flex justify-start mb-1">
							<span className={`text-[9px] px-1.5 py-0.5 rounded text-white font-medium ${
								isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
							}`}>
								Trailer ready
							</span>
						</div>
					)}
					{isCertRequired && (
						<div className="flex justify-start mb-1">
							<span className={`text-[9px] px-1.5 py-0.5 rounded text-white font-medium ${
								isDarkMode ? 'bg-blue-600' : 'bg-blue-500'
							}`}>
								Certificate
							</span>
						</div>
					)}
					<p className="text-xs text-left">{deadheadDistance || 0} mi</p>
					<p className="md:block hidden text-gray-500 text-[10px] text-left">
						deadhead
					</p>
				</div>
				<div className="md:flex md:flex-row md:col-span-4 md:gap-8 gap-1">
					<div className="flex items-center md:flex-1 md:min-w-0">
						<span className="badge badge-accent badge-md mr-2 !hidden md:!inline">
							1
						</span>

						<div>
							<p
								data-tooltip-delay-show="800"
								data-tooltip-place="top"
								data-tooltip-id={load._id}
								data-tooltip-content="pickup"
								onMouseEnter={() => setHoverPickup(true)}
								onMouseLeave={() => setHoverPickup(false)}
								className="text-xs"
							>
								<span className="md:!hidden text-lg">1. </span>
								{pickupLabel && (
									pickupLabel.length <= 4 ? (
										<span
											className="underline decoration-dotted cursor-pointer"
											onClick={e => {
												e.stopPropagation()
												onFacilityClick?.(load.pickupLabel)
											}}
										>
											{pickupLabel}
										</span>
									) : (
										<span>{pickupLabel}</span>
									)
								)}{' '}
								{pickupCity}
							</p>
							<div className="flex items-center gap-1">
								{load?.arrivalWindows && load.arrivalWindows.length > 0 && (
									<PencilIcon
										className="w-3 h-3"
										style={{
											color: isDarkMode ? '#adbac7' : '#6b7280',
											minWidth: '12px',
											minHeight: '12px',
										}}
										data-tooltip-delay-show="500"
										data-tooltip-place="top"
										data-tooltip-id={`${load._id}-arrival-windows`}
										data-tooltip-content="arrival"
									/>
								)}
								<p className="text-gray-500 text-[10px]">{pickupTime}</p>
							</div>
						</div>
					</div>

					<div className="flex items-center md:flex-1 md:min-w-0">
						<span className="badge badge-accent badge-md mr-2 !hidden md:!inline">
							{loadStops}
						</span>

						<div>
							<p
								data-tooltip-delay-show="800"
								data-tooltip-place="top"
								data-tooltip-id={load._id}
								data-tooltip-content="dropoff"
								onMouseEnter={() => setHoverDropoff(true)}
								onMouseLeave={() => setHoverDropoff(false)}
								className="text-xs"
							>
								<span className="md:!hidden text-lg">{loadStops}. </span>
								{deliveryLabel && (
									deliveryLabel.length <= 4 ? (
										<span
											className="underline decoration-dotted cursor-pointer"
											onClick={e => {
												e.stopPropagation()
												onFacilityClick?.(load.deliveryLabel)
											}}
										>
											{deliveryLabel}
										</span>
									) : (
										<span>{deliveryLabel}</span>
									)
								)}{' '}
								{deliveryCity}
							</p>
							<p className="text-gray-500 text-[10px]">{deliveryDate}</p>
						</div>
					</div>
				</div>
				<div className={`${showQuickPost ? 'md:col-span-5' : 'md:col-span-7'} md:grid md:grid-cols-7 md:grid-rows-none items-center md:gap-4`}>
					<div className="col-span-1 md:block flex flex-col justify-center">
						<p
							style={{
								...lastSeenStyle,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: '1px 4px',
							}}
							data-tooltip-delay-show="800"
							data-tooltip-place="top"
							data-tooltip-id={load._id}
							data-tooltip-content="lastSeen"
							className="text-[9px]"
						>
							{lastSeenDate}
						</p>
						<p className="text-gray-500 text-[10px]">Last seen</p>
					</div>

					<div className="col-span-1 md:block flex flex-col justify-center">
						<p className="text-xs text-center">
							{distance ? distance?.toFixed(0) : 0} mi
						</p>
						<p className="text-gray-500 text-[10px] text-center">
							{transitDiff}
						</p>
					</div>

					<div className="col-span-1 md:block flex flex-col justify-center">
						<p className="text-xs text-center">{driverType || 'N/A'}</p>
						<p className="text-gray-500 text-[10px] text-center">Driver</p>
					</div>

					<div className="col-span-1 md:col-span-2 md:block flex flex-col justify-center">
						<p
							className={`text-xs text-center ${
								isTrailerWarning ? 'text-red-500 font-semibold' : ''
							}`}
						>
							{formatTrailerTypes(trailerTypes)}
							{isTrailerWarning && <span className="ml-1">!</span>}
						</p>
						<p
							className={`text-[10px] text-center ${
								isTrailerRequiredWarning
									? 'text-red-500 font-semibold'
									: 'text-gray-500'
							}`}
						>
							{isTrailerRequiredWarning && <span>! </span>}
							{trailerProvided || 'Trailer'}
						</p>
					</div>

					<div className="col-span-1 md:block flex flex-col justify-center md:ml-4">
						<p className="text-xs text-center flex items-center justify-center gap-1 whitespace-nowrap">
							{formatLoadingType(loadingType)}
							{load?.isBobtail && (
								<span
									className={`text-[10px] ${
										isDarkMode ? 'text-blue-400' : 'text-blue-600'
									} font-medium text-bold`}
								>
									Bobtail
								</span>
							)}
							{load?.isAIEnabled && (
								<span
									className={`text-[10px] ${
										isDarkMode ? 'text-purple-400' : 'text-purple-500'
									} font-medium text-bold`}
								>
									AI
								</span>
							)}
						</p>
						<p className="text-gray-500 text-[10px] text-center whitespace-nowrap">
							Load Type
						</p>
					</div>

					<div className={`col-span-1 md:block flex flex-col justify-center ${showQuickPost ? 'relative z-10 md:ml-14' : 'md:text-right'}`}>
						{load.isPriceIncrease ? (
							<>
								<div className={`flex items-center justify-start gap-1 ${showQuickPost ? '' : 'md:justify-end'}`}>
									<span
										className={`text-xs text-left font-bold ${showQuickPost ? '' : 'md:text-right'}`}
										style={{ color: isDarkMode ? '#4ade80' : '#16a34a' }}
										onMouseEnter={() => setHoverPrice(true)}
										onMouseLeave={() => setHoverPrice(false)}
										{...(priceTip && {
											'data-tooltip-delay-show': '500',
											'data-tooltip-place': 'left',
											'data-tooltip-id': `${load._id}-price`,
											'data-tooltip-content': 'price',
										})}
									>
										{priceText}
									</span>
									{showPriorityBadge && (
										<LockClosedIcon
											className="w-4 h-4"
											style={{
												color: isDarkMode ? '#ffffff' : '#000000',
												minWidth: '16px',
												minHeight: '16px',
											}}
											data-tooltip-delay-show="500"
											data-tooltip-place="top"
											data-tooltip-id={`${load._id}-priority`}
											data-tooltip-content="priority"
										/>
									)}
								</div>
								<p className={`text-gray-500 text-[10px] text-left ${showQuickPost ? '' : 'md:text-right'}`}>
									<span className="inline-flex items-center gap-1">
										<span style={{ color: isDarkMode ? '#4ade80' : '#16a34a' }}>
											↑${Math.ceil(load.priceDifference)}
										</span>
										<span className="text-gray-400 line-through">
											${(price - load.priceDifference)?.toFixed(2)}
										</span>
									</span>{' '}
									{ppmText}
								</p>
							</>
						) : (
							<>
								<div className={`flex items-center justify-start gap-1 ${showQuickPost ? '' : 'md:justify-end'}`}>
									<p
										className={`text-xs text-left ${showQuickPost ? '' : 'md:text-right'}`}
										onMouseEnter={() => setHoverPrice(true)}
										onMouseLeave={() => setHoverPrice(false)}
										{...(priceTip && {
											'data-tooltip-delay-show': '500',
											'data-tooltip-place': 'left',
											'data-tooltip-id': `${load._id}-price`,
											'data-tooltip-content': 'price',
										})}
									>
										{priceText}
									</p>
									{showPriorityBadge && (
										<LockClosedIcon
											className="w-4 h-4"
											style={{
												color: isDarkMode ? '#ffffff' : '#000000',
												minWidth: '16px',
												minHeight: '16px',
											}}
											data-tooltip-delay-show="500"
											data-tooltip-place="top"
											data-tooltip-id={`${load._id}-priority`}
											data-tooltip-content="priority"
										/>
									)}
								</div>
								<p
									className={`text-gray-500 text-[10px] text-left ${showQuickPost ? '' : 'md:text-right'}`}
									onMouseEnter={() => setHoverPrice(true)}
									onMouseLeave={() => setHoverPrice(false)}
									{...(priceTip && {
										'data-tooltip-delay-show': '500',
										'data-tooltip-place': 'left',
										'data-tooltip-id': `${load._id}-price`,
										'data-tooltip-content': 'price',
									})}
								>
									{ppmText}
								</p>
							</>
						)}
					</div>

					</div>

				{showQuickPost && (
					<div className="md:flex col-span-2 items-center justify-end">
						<QuickPostButton
							load={load}
							isDarkMode={isDarkMode}
							isActive={isQuickPostActive}
							onActivate={() => setIsQuickPostActive(true)}
							onDeactivate={() => setIsQuickPostActive(false)}
						/>
					</div>
				)}
				</>
				)}
			</div>

			<Tooltip
				id={load._id}
				openOnClick={isMobile}
				clickable={isMobile}
				render={({ content }) =>
					content === 'pickup'
						? pickupTip
						: content === 'dropoff'
							? dropoffTip
							: lastSeenTip
				}
			/>

			<Tooltip
				id={`${load._id}-price`}
				style={{
					zIndex: 9999,
				}}
				openOnClick={isMobile}
				clickable={isMobile}
				render={() => priceTip}
			/>

			{showPriorityBadge && (
				<Tooltip
					id={`${load._id}-priority`}
					style={{
						zIndex: 9999,
					}}
					render={() => priorityTip}
				/>
			)}

			{load?.arrivalWindows && load.arrivalWindows.length > 0 && (
				<Tooltip
					id={`${load._id}-arrival-windows`}
					style={{
						zIndex: 9999,
					}}
					render={() => arrivalWindowsTip}
				/>
			)}

			{!isCollapsed && (
				<div
					className="border border-t shadow-[0_1px_3px_rgba(0,0,0,0.08)] rounded-b-lg overflow-hidden"
					style={styles}
				>
					{lockedContent ?? (
						<LoadCollapse
							load={load}
							isDarkMode={isDarkMode}
							styles={styles}
							onPostTruck={onPostTruck}
							onFacilityClick={onFacilityClick}
							hideShare={hideShare}
							isSubscribed={isSubscribed}
							onUpgradeRequired={onUpgradeRequired}
						/>
					)}
				</div>
			)}
		</div>
	)
}
